import * as React from "react";
import { Kbd } from "@/components/ui/kbd";
import { useEffect, useMemo, useRef, useState } from "react";

import { useAllBrands } from "@/hooks/use-brands";
import { Textarea } from "@/components/ui/textarea";
import { usePeoplePool } from "@/components/people/person-picker";
import { groupPeople, matchPeople, type PersonPickerLead } from "@/components/people/person-picker-order";
import { PeopleGroupHeading, PersonRowBody, type PersonOption } from "@/components/people/person-picker-view";
import { PEOPLE_POOLS, type PeoplePool } from "@/services/profile-services";
import { cn } from "@/lib/utils";
import { activeMentionToken, insertMention, type MentionCandidate } from "./mentions";

type MentionTextareaProps = Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange"> & {
  value: string;
  onChange: (value: string) => void;
  /** The registered people pool the box lists (``backend/app/core/people_pools.py``). */
  pool: PeoplePool;
  /** The record's tenant, for a record pool. */
  tenantId?: string;
  /** Fetch the pool only once someone starts writing. */
  enabled?: boolean;
  /** People to list first under their own heading (the record's people). */
  lead?: PersonPickerLead;
  /** Brands to list before the rest (the record's brand). */
  firstTenantIds?: string[];
  /** Fires with the candidate the user picked; the parent keeps the ids. */
  onMentionPick?: (candidate: MentionCandidate) => void;
  /** Cmd/Ctrl+Enter. */
  onSubmit?: () => void;
  /** Where the suggestion list opens relative to the textarea. */
  suggestionsPlacement?: "above" | "below";
};

/**
 * Textarea that opens the people picker's mention mode when the caret sits on
 * a `@word` token. The list is the pool's people, grouped and ranked exactly as
 * PersonPicker does, drawn with the standard row. Arrow keys move through it;
 * Enter/Tab commits; Escape dismisses it for that `@`. With no match the list
 * hides so the `@` stays free text.
 */
export function MentionTextarea({
  value,
  onChange,
  pool,
  tenantId,
  enabled = true,
  lead,
  firstTenantIds,
  onMentionPick,
  onSubmit,
  suggestionsPlacement = "above",
  ...textareaProps
}: MentionTextareaProps) {
  const [caret, setCaret] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);
  // The `@` position the user dismissed with Escape; a new `@` reopens.
  const [dismissedAtIdx, setDismissedAtIdx] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { data: people = [] } = usePeoplePool(pool, tenantId, { enabled });
  const { data: brands = [] } = useAllBrands(enabled);

  const token = useMemo(() => activeMentionToken(value, caret), [value, caret]);
  const tokenAtIdx = token?.atIdx ?? null;
  const query = token?.query ?? null;

  const brandNames = useMemo(() => Object.fromEntries(brands.map((b) => [b.id, b.name])), [brands]);
  const groups = useMemo(() => {
    if (query == null) return [];
    return groupPeople(matchPeople(people as PersonOption[], query), brandNames, { lead, firstTenantIds });
  }, [query, people, brandNames, lead, firstTenantIds]);
  // Keyboard order: the groups top to bottom.
  const matches = useMemo(() => groups.flatMap((g) => g.people), [groups]);

  useEffect(() => {
    setActiveIdx(0);
  }, [query, matches.length]);

  useEffect(() => {
    if (tokenAtIdx === null) setDismissedAtIdx(null);
  }, [tokenAtIdx]);

  const pickerOpen = tokenAtIdx !== null && tokenAtIdx !== dismissedAtIdx && matches.length > 0;

  // Escape must close the list, not the Sheet/Dialog around the composer.
  // Radix listens for Escape on `document` in the capture phase, which runs
  // before any handler on the textarea — so intercept on `window` (captured
  // earlier still) while the list is open and the textarea has focus.
  useEffect(() => {
    if (!pickerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || document.activeElement !== textareaRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      setDismissedAtIdx(tokenAtIdx);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [pickerOpen, tokenAtIdx]);

  // Keep the keyboard row in view as it moves.
  const listRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: "nearest" });
  }, [activeIdx]);

  const commitMention = (p: PersonOption) => {
    const c: MentionCandidate = { id: p.id, display_name: p.full_name, email: p.email };
    const next = insertMention(value, caret, c);
    if (!next) return;
    onChange(next.value);
    onMentionPick?.(c);
    setCaret(next.caret);
    // Refocus after the state update so the user can keep typing.
    setTimeout(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(next.caret, next.caret);
    }, 0);
  };

  const syncCaret = (e: React.SyntheticEvent<HTMLTextAreaElement>) =>
    setCaret(e.currentTarget.selectionStart ?? e.currentTarget.value.length);

  const showHeadings = groups.length > 1;
  let index = -1;

  return (
    <div className="relative">
      <Textarea
        {...textareaProps}
        ref={textareaRef}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setCaret(e.target.selectionStart ?? e.target.value.length);
        }}
        onKeyUp={syncCaret}
        onClick={syncCaret}
        aria-expanded={pickerOpen}
        aria-autocomplete="list"
        onKeyDown={(e) => {
          if (pickerOpen) {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActiveIdx((i) => Math.min(i + 1, matches.length - 1));
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setActiveIdx((i) => Math.max(i - 1, 0));
              return;
            }
            if (e.key === "Enter" || e.key === "Tab") {
              e.preventDefault();
              commitMention(matches[activeIdx]);
              return;
            }
          }
          if (onSubmit && e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            onSubmit();
          }
        }}
      />
      {pickerOpen && (
        <div
          role="listbox"
          aria-label="Mention a teammate"
          data-testid="mention-suggestions"
          className={cn(
            "absolute left-0 z-10 flex w-full flex-col overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md",
            suggestionsPlacement === "above" ? "bottom-full mb-1" : "top-full mt-1",
          )}
        >
          <div className="flex items-center justify-between border-b px-3 py-1.5">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Mention a teammate
            </span>
            <span className="hidden text-[11px] text-muted-foreground sm:inline" aria-hidden>
              <Kbd tone="plain">↑↓</Kbd> <Kbd tone="plain">Enter</Kbd>
            </span>
          </div>
          <div ref={listRef} className="max-h-64 overflow-y-auto p-1">
            {groups.map(({ company, people: group }) => (
              <div key={company} role="group" aria-label={showHeadings ? company : undefined}>
                {showHeadings ? <PeopleGroupHeading>{company}</PeopleGroupHeading> : null}
                {group.map((p) => {
                  index += 1;
                  const i = index;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      role="option"
                      aria-selected={i === activeIdx}
                      className={cn(
                        "flex min-h-13 w-full min-w-0 items-center gap-2.5 rounded-sm px-2 py-1 text-left sm:min-h-9",
                        i === activeIdx ? "bg-accent" : "hover:bg-muted",
                      )}
                      onMouseEnter={() => setActiveIdx(i)}
                      onMouseDown={(e) => {
                        // mouseDown so the textarea doesn't blur before the click.
                        e.preventDefault();
                        commitMention(p);
                      }}
                    >
                      <PersonRowBody person={p} />
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="border-t px-3 py-1.5 text-[11px] text-muted-foreground">{PEOPLE_POOLS[pool]}</div>
        </div>
      )}
    </div>
  );
}
